import { useState, useEffect } from 'react'
import { CreditCardIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import api from '@/lib/api'
import { useAuthStore } from '@/lib/auth'
import { uiLogger } from '@/lib/debug'
import { buildPackageRenewalAcceptance, PACKAGE_RECURRING_AUTHORIZATION_TEXT, type PackageRenewalOffer } from '@/lib/policyConsent'

declare global {
  interface Window {
    Stripe?: any
    __RUNTIME_CONFIG__?: {
      STRIPE_PUBLISHABLE_KEY?: string
    }
  }
}

/**
 * Legacy account-wide card gate. Enrolled v2 package owners can use their
 * optional-card trial and recovery/billing surfaces without this overlay;
 * legacy deployment controls retain the separate card-required flag.
 * Rendered alongside ForcePasswordChangeModal in App.tsx.
 */
export default function RequirePaymentMethodGate() {
  const { requiresPaymentMethod, packageCardOptional, user } = useAuthStore()
  const isStaff = user?.root_admin || user?.role === 'sre'
  if (!requiresPaymentMethod || packageCardOptional || isStaff) return null
  return <PaymentMethodGateForm />
}

function PaymentMethodGateForm() {
  const { refreshPaymentMethodGate } = useAuthStore()
  const [loading, setLoading] = useState(false)
  const [stripe, setStripe] = useState<any>(null)
  const [cardElement, setCardElement] = useState<any>(null)
  const [postalCode, setPostalCode] = useState('')
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [elementsReady, setElementsReady] = useState(false)
  const [initializing, setInitializing] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [renewalOffers, setRenewalOffers] = useState<PackageRenewalOffer[]>([])
  const [authorizeRenewal, setAuthorizeRenewal] = useState(false)
  const [cardAttached, setCardAttached] = useState(false)

  useEffect(() => {
    const stripePublishableKey = window.__RUNTIME_CONFIG__?.STRIPE_PUBLISHABLE_KEY

    if (!stripePublishableKey) {
      uiLogger.error('Stripe publishable key not configured')
      setError('Payment configuration error. Please contact support.')
      return
    }

    if (window.Stripe && !stripe) {
      setStripe(window.Stripe(stripePublishableKey))
    }

    ;(async () => {
      try {
        const [{ client_secret }, offers] = await Promise.all([
          api.createAddPaymentMethodIntent(), api.getPackageRenewalOffers().catch(() => []),
        ])
        setClientSecret(client_secret)
        setRenewalOffers(offers)
      } catch (e) {
        uiLogger.error('Failed to create setup intent', e)
        setError('Unable to start payment setup. Please try refreshing the page.')
      }
    })()
  }, [])

  // Mount Stripe card elements
  useEffect(() => {
    if (!stripe || cardElement) return

    const elementsInstance = stripe.elements()

    const style = {
      base: {
        fontSize: '16px',
        color: '#ffffff',
        '::placeholder': { color: 'rgba(255, 255, 255, 0.3)' },
      },
      invalid: { color: '#ef4444' },
    }

    const cardNumberElement = elementsInstance.create('cardNumber', { style, placeholder: '1234 1234 1234 1234' })
    const cardExpiryElement = elementsInstance.create('cardExpiry', { style, placeholder: 'MM / YY' })
    const cardCvcElement = elementsInstance.create('cardCvc', { style, placeholder: 'CVC' })

    setCardElement({ cardNumber: cardNumberElement, cardExpiry: cardExpiryElement, cardCvc: cardCvcElement })

    let readyCount = 0
    const markReady = () => {
      readyCount += 1
      if (readyCount >= 3) setElementsReady(true)
    }
    cardNumberElement.on?.('ready', markReady)
    cardExpiryElement.on?.('ready', markReady)
    cardCvcElement.on?.('ready', markReady)

    setTimeout(() => {
      document.getElementById('gate-card-number')  && cardNumberElement.mount('#gate-card-number')
      document.getElementById('gate-card-expiry')  && cardExpiryElement.mount('#gate-card-expiry')
      document.getElementById('gate-card-cvc')     && cardCvcElement.mount('#gate-card-cvc')
    }, 100)
  }, [stripe, cardElement])

  useEffect(() => {
    if (clientSecret && elementsReady) setInitializing(false)
  }, [clientSecret, elementsReady])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!cardAttached && (!stripe || !cardElement?.cardNumber)) return

    setLoading(true)
    setError(null)

    try {
      if (cardAttached) {
        if (!await refreshPaymentMethodGate()) {
          setError('Your card was added, but its account status is not available yet. Check payment status again.')
          return
        }
        toast.success('Payment method added — welcome!')
        return
      }
      let secret = clientSecret
      if (!secret) {
        const { client_secret } = await api.createAddPaymentMethodIntent()
        secret = client_secret
        setClientSecret(client_secret)
      }

      const { setupIntent, error: stripeError } = await stripe.confirmCardSetup(secret, {
        payment_method: {
          card: cardElement.cardNumber,
          billing_details: { address: { postal_code: postalCode || undefined } },
        },
      })

      if (stripeError) throw new Error(stripeError.message)

      await api.addPaymentMethod(
        setupIntent.payment_method,
        authorizeRenewal ? renewalOffers.map(buildPackageRenewalAcceptance) : [],
      )

      setCardAttached(true)
      if (!await refreshPaymentMethodGate()) {
        setError('Your card was added, but its account status is not available yet. Check payment status again.')
        return
      }
      toast.success('Payment method added — welcome!')
    } catch (err: any) {
      uiLogger.error('Failed to add payment method', err)
      setError(err.message || 'Failed to add payment method')
    } finally {
      setLoading(false)
    }
  }

  const fieldClass =
    'block w-full rounded-lg border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 px-3 py-3 text-gray-900 dark:text-white placeholder-white/30 focus-within:border-blue-500/50 focus-within:ring-1 focus-within:ring-blue-500/50'

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="w-full max-w-md mx-4 bg-gray-50 dark:bg-[#18181b] border border-gray-200 dark:border-white/10 rounded-2xl shadow-2xl p-8">
        <div className="flex flex-col items-center mb-6">
          <div className="w-12 h-12 rounded-full bg-blue-500/12 flex items-center justify-center mb-4">
            <CreditCardIcon className="w-6 h-6 text-blue-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Add a Payment Method</h2>
          <p className="text-sm text-gray-500 dark:text-white/50 mt-2 text-center">
            A payment method is required to continue.
            Adding a card alone does not authorize a package renewal.
          </p>
        </div>

        {error && (
          <div className="mb-4 rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-400">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs text-gray-500 dark:text-white/50 mb-1">Card Number</label>
            <div className="relative">
              <div id="gate-card-number" className={fieldClass} />
              {initializing && <div className="pointer-events-none absolute inset-0 rounded-lg bg-gray-50 dark:bg-white/5 animate-pulse" />}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs text-gray-500 dark:text-white/50 mb-1">MM / YY</label>
              <div className="relative">
                <div id="gate-card-expiry" className={fieldClass} />
                {initializing && <div className="pointer-events-none absolute inset-0 rounded-lg bg-gray-50 dark:bg-white/5 animate-pulse" />}
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 dark:text-white/50 mb-1">CVC</label>
              <div className="relative">
                <div id="gate-card-cvc" className={fieldClass} />
                {initializing && <div className="pointer-events-none absolute inset-0 rounded-lg bg-gray-50 dark:bg-white/5 animate-pulse" />}
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 dark:text-white/50 mb-1">ZIP / Postal</label>
              <input
                type="text"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                autoComplete="postal-code"
                inputMode="numeric"
                disabled={initializing}
                className="block w-full h-[45px] box-border rounded-lg border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 px-3 text-base text-gray-900 dark:text-white placeholder:text-white/30 focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 disabled:opacity-70"
                placeholder="90210"
              />
            </div>
          </div>

          {renewalOffers.some(offer => offer.public_terms?.recovery?.copy || offer.public_terms?.backups?.copy) && (
            <div className="space-y-2 text-xs text-gray-300" aria-label="Package renewal disclosures">
              {renewalOffers.filter(offer => offer.public_terms?.recovery?.copy || offer.public_terms?.backups?.copy).map(offer => <div key={offer.server_uuid}>
                <strong>{offer.display_name}</strong>
                {offer.public_terms?.recovery?.copy && <p>{offer.public_terms.recovery.copy}</p>}
                {offer.public_terms?.backups?.copy && <p>{offer.public_terms.backups.copy}</p>}
              </div>)}
            </div>
          )}
          {renewalOffers.length > 0 && (
            <label className="flex items-start gap-3 rounded-lg border border-emerald-500/20 bg-emerald-500/[0.06] p-3 text-xs text-gray-300">
              <input type="checkbox" checked={authorizeRenewal} onChange={event => setAuthorizeRenewal(event.target.checked)} className="mt-0.5" />
              <span><strong>Continue package service after the trial.</strong> {PACKAGE_RECURRING_AUTHORIZATION_TEXT} Adding the card without checking this box does not authorize renewal.</span>
            </label>
          )}

          <button
            type="submit"
            disabled={loading || initializing}
            className="w-full rounded-lg bg-blue-600 px-4 py-3 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Checking...' : cardAttached ? 'Check payment method status' : 'Add Payment Method'}
          </button>
        </form>
      </div>
    </div>
  )
}
